import React, { useState, useEffect, useCallback } from 'react';
import {
  FiAward, FiEdit, FiList, FiGrid, FiX, FiSave,
  FiAlertCircle, FiRefreshCw, FiClock, FiCalendar, FiCheckCircle,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './LifecycleTracker.css';

const GRANT_STATUSES = [
  { key: 'draft', label: 'Draft', color: '#6b7280' },
  { key: 'submitted', label: 'Submitted', color: '#2563eb' },
  { key: 'under_review', label: 'Under Review', color: '#f59e0b' },
  { key: 'scored', label: 'Scored', color: '#8b5cf6' },
  { key: 'funded', label: 'Funded', color: '#10b981' },
  { key: 'rejected', label: 'Rejected', color: '#ef4444' },
  { key: 'resubmission', label: 'Resubmission', color: '#f97316' },
];

const PAPER_STATUSES = [
  { key: 'draft', label: 'Draft', color: '#6b7280' },
  { key: 'submitted', label: 'Submitted', color: '#2563eb' },
  { key: 'under_review', label: 'Under Review', color: '#f59e0b' },
  { key: 'revision_requested', label: 'Revision', color: '#f97316' },
  { key: 'accepted', label: 'Accepted', color: '#10b981' },
  { key: 'rejected', label: 'Rejected', color: '#ef4444' },
  { key: 'resubmission', label: 'Resubmission', color: '#8b5cf6' },
];

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const diff = new Date(isoDate) - Date.now();
  return Math.ceil(diff / 86400000);
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

function getEffectiveStatus(item, type) {
  if (item.submission_status) return item.submission_status;
  if (type === 'grant') {
    if (item.funding_outcome === 'funded') return 'funded';
    if (item.funding_outcome === 'rejected') return 'rejected';
  }
  if (item.current_stage === 'done') return 'draft';
  return 'draft';
}

const LifecycleTracker = () => {
  const [proposals, setProposals] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('board'); // 'board' | 'list'
  const [filter, setFilter] = useState('all'); // 'all' | 'grants' | 'papers'
  const [editingItem, setEditingItem] = useState(null); // { type, item }
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pRes, dRes] = await Promise.all([
        fetch(getApiEndpoint('/api/grant-proposals'), { headers: getAuthHeaders() }),
        fetch(getApiEndpoint('/api/paper-drafts'), { headers: getAuthHeaders() }),
      ]);
      if (pRes.ok) setProposals(await pRes.json());
      if (dRes.ok) setDrafts(await dRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEdit = (type, item) => {
    setEditingItem({ type, item });
    setEditForm({
      submission_status: item.submission_status || getEffectiveStatus(item, type),
      submission_date: item.submission_date ? item.submission_date.split('T')[0] : '',
      decision_deadline: item.decision_deadline ? item.decision_deadline.split('T')[0] : '',
    });
  };

  const saveStatus = async () => {
    if (!editingItem) return;
    setSaving(true);
    const { type, item } = editingItem;
    const endpoint = type === 'grant'
      ? `/api/grant-proposals/${item.id}/submission`
      : `/api/paper-drafts/${item.id}/submission`;
    try {
      const res = await fetch(getApiEndpoint(endpoint), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_status: editForm.submission_status,
          submission_date: editForm.submission_date || null,
          decision_deadline: editForm.decision_deadline || null,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      if (type === 'grant') {
        setProposals(prev => prev.map(p => p.id === item.id ? updated : p));
      } else {
        setDrafts(prev => prev.map(d => d.id === item.id ? updated : d));
      }
      setEditingItem(null);
    } catch (_) {}
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="lt-loading"><div className="spinner" /><p>Loading tracker...</p></div>
    );
  }

  if (error) {
    return (
      <div className="lt-error">
        <FiAlertCircle /> {error}
        <button onClick={fetchData}><FiRefreshCw /> Retry</button>
      </div>
    );
  }

  // Build unified items list
  const items = [];
  if (filter !== 'papers') {
    proposals.forEach(p => items.push({
      ...p, _type: 'grant', _status: getEffectiveStatus(p, 'grant'),
    }));
  }
  if (filter !== 'grants') {
    drafts.forEach(d => items.push({
      ...d, _type: 'paper', _status: getEffectiveStatus(d, 'paper'),
    }));
  }

  const statuses = filter === 'papers' ? PAPER_STATUSES :
                   filter === 'grants' ? GRANT_STATUSES :
                   GRANT_STATUSES; // use grant statuses as superset for "all"

  const renderCard = (item) => {
    const deadline = daysUntil(item.decision_deadline);
    return (
      <div
        key={`${item._type}-${item.id}`}
        className={`lt-card lt-card--${item._type}`}
        onClick={() => openEdit(item._type, item)}
      >
        <div className="lt-card-type">{item._type === 'grant' ? 'Grant' : 'Paper'}</div>
        <div className="lt-card-title">{item.title}</div>
        {item._type === 'grant' && item.nofo_number && (
          <span className="lt-card-nofo">{item.nofo_number}</span>
        )}
        {item._type === 'paper' && item.target_venue && (
          <span className="lt-card-venue">{item.target_venue}</span>
        )}
        <div className="lt-card-footer">
          <span className="lt-card-updated">{timeAgo(item.updated_at)}</span>
          {deadline !== null && (
            <span className={`lt-card-deadline ${deadline < 0 ? 'lt-deadline--overdue' : deadline < 14 ? 'lt-deadline--soon' : ''}`}>
              {deadline < 0 ? `${Math.abs(deadline)}d overdue` : deadline === 0 ? 'Due today' : `${deadline}d left`}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="lt-page">

      {/* Header */}
      <div className="lt-header">
        <h2><FiCalendar /> Lifecycle Tracker</h2>
        <div className="lt-controls">
          <div className="lt-filter-tabs">
            {['all', 'grants', 'papers'].map(f => (
              <button key={f} className={`lt-tab ${filter === f ? 'lt-tab--active' : ''}`}
                onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'grants' ? 'Proposals' : 'Papers'}
              </button>
            ))}
          </div>
          <div className="lt-view-toggle">
            <button className={`lt-view-btn ${viewMode === 'board' ? 'lt-view-btn--active' : ''}`}
              onClick={() => setViewMode('board')} title="Board view">
              <FiGrid />
            </button>
            <button className={`lt-view-btn ${viewMode === 'list' ? 'lt-view-btn--active' : ''}`}
              onClick={() => setViewMode('list')} title="List view">
              <FiList />
            </button>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="lt-empty">
          <p>No proposals or papers yet. Start writing to see them tracked here.</p>
        </div>
      ) : viewMode === 'board' ? (
        /* ── Board view ──────────────────────────────────────────────── */
        <div className="lt-board">
          {statuses.map(col => {
            const colItems = items.filter(i => i._status === col.key);
            return (
              <div key={col.key} className="lt-column">
                <div className="lt-column-header" style={{ borderTopColor: col.color }}>
                  <span className="lt-column-title">{col.label}</span>
                  <span className="lt-column-count">{colItems.length}</span>
                </div>
                <div className="lt-column-body">
                  {colItems.map(renderCard)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── List view ───────────────────────────────────────────────── */
        <div className="lt-list-section">
          <table className="lt-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Deadline</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const statusDef = statuses.find(s => s.key === item._status) || statuses[0];
                const deadline = daysUntil(item.decision_deadline);
                return (
                  <tr key={`${item._type}-${item.id}`}>
                    <td>
                      <span className={`lt-type-badge lt-type-badge--${item._type}`}>
                        {item._type === 'grant' ? <FiAward /> : <FiEdit />}
                        {item._type === 'grant' ? 'Grant' : 'Paper'}
                      </span>
                    </td>
                    <td className="lt-list-title">
                      {item.title}
                      {item.nofo_number && <span className="lt-list-sub">{item.nofo_number}</span>}
                      {item.target_venue && <span className="lt-list-sub">{item.target_venue}</span>}
                    </td>
                    <td>
                      <span className="lt-status-badge" style={{ background: statusDef.color + '20', color: statusDef.color }}>
                        {statusDef.label}
                      </span>
                    </td>
                    <td className="lt-list-date">{item.submission_date ? new Date(item.submission_date).toLocaleDateString() : '—'}</td>
                    <td className="lt-list-date">
                      {deadline !== null ? (
                        <span className={deadline < 0 ? 'lt-deadline--overdue' : deadline < 14 ? 'lt-deadline--soon' : ''}>
                          {deadline < 0 ? `${Math.abs(deadline)}d overdue` : `${deadline}d`}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="lt-list-date">{timeAgo(item.updated_at)}</td>
                    <td>
                      <button className="lt-edit-btn" onClick={() => openEdit(item._type, item)}>
                        <FiEdit />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit modal ───────────────────────────────────────────────── */}
      {editingItem && (
        <div className="lt-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="lt-modal" onClick={e => e.stopPropagation()}>
            <div className="lt-modal-header">
              <h3>Update Status</h3>
              <button className="lt-modal-close" onClick={() => setEditingItem(null)}><FiX /></button>
            </div>
            <p className="lt-modal-title">{editingItem.item.title}</p>

            <div className="lt-modal-form">
              <div className="lt-modal-field">
                <label>Status</label>
                <select value={editForm.submission_status}
                  onChange={e => setEditForm(prev => ({ ...prev, submission_status: e.target.value }))}>
                  {(editingItem.type === 'grant' ? GRANT_STATUSES : PAPER_STATUSES).map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="lt-modal-field">
                <label><FiCalendar /> Submission Date</label>
                <input type="date" value={editForm.submission_date}
                  onChange={e => setEditForm(prev => ({ ...prev, submission_date: e.target.value }))} />
              </div>
              <div className="lt-modal-field">
                <label><FiClock /> Decision Deadline</label>
                <input type="date" value={editForm.decision_deadline}
                  onChange={e => setEditForm(prev => ({ ...prev, decision_deadline: e.target.value }))} />
              </div>
            </div>

            <div className="lt-modal-actions">
              <button className="lt-btn lt-btn--primary" onClick={saveStatus} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="lt-btn lt-btn--outline" onClick={() => setEditingItem(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifecycleTracker;
